/**
 * MotifMenuOptions.js
 * Created by calhosh on 7/6/2021.
 */
import React, {useMemo, useCallback} from 'react';
import {useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
import styled from 'styled-components';
import {labels} from '../../../util/utils';
import env from '../../../util/env';
import {documentTypes, smartScreenRoutes, gaRoleTypes} from '../../../util/uiconstants';
import EllipseControl from '../EllipsesControl/EllipsesControl';
import MenuItemGroup from '@ey/motif-react-wrapper/MenuItemGroup';
import MenuItem from '@ey/motif-react-wrapper/MenuItem';
import {useIsDrawerMode} from '../../../util/Ares/customHooks';
import {useQualtrics} from '@ey/canvascoreservices/Qualtrics.js';
import {toggleFrameModal, frameEvents} from '@ey/canvascoreservices/ReactiveFrames';

export default function MotifMenuOptions(props) {
	const {baseCanvasUrl, engagementId, engagementDescription, featureConfigurations, gaRoleTypeId, quickLinks, engagement, isOfflineMode} = props;

	const isAresView = useSelector((store) => store.isAresView),
		configurations = useSelector((store) => store.configurations);

	const configFeatures = configurations && configurations?.features && configurations?.features;
	const isDrawerMode = useIsDrawerMode();

	const projectId = configFeatures
		? configFeatures.qualtrics
			? isAresView
				? configFeatures.qualtrics.aresQualtricsProjectId
				: configFeatures.qualtrics.kronosQualtricsProjectId
			: undefined
		: undefined;

	const qualtricsInterceptAPI = useQualtrics(projectId);

	const showQualtricsLink =
		qualtricsInterceptAPI &&
		projectId &&
		!isDrawerMode &&
		(isAresView ? configFeatures?.qualtrics?.isAresQualtricsEnabled : configFeatures?.qualtrics?.isKronosQualtricsEnabled === true);

	const onLeaveUsFeedbackClick = (e) => {
		qualtricsInterceptAPI((API) => {
			API.unload();
			API.load().done(runQualtricsIntercept);
		});
		e.preventDefault();
	};

	const runQualtricsIntercept = () => {
		qualtricsInterceptAPI((API) => {
			API.run();
		});
	};

	const renderQualtricsMotifLinkOption = useCallback((title) => {
		return (
			<MenuItem
				onClick={() => {
					onOptionClick();
				}}
			>
				<Link to={'/'} onClick={onLeaveUsFeedbackClick}>
					<EllipseControl content={title} tooltip={title} noOfLines={1} />
				</Link>
			</MenuItem>
		);
	}, []);

	const onOptionClick = useCallback(() => {
		toggleFrameModal({
			frameArgs: {
				event: frameEvents.TOGGLE_DRAWER,
				open: false,
				isMoreMenuClicked: true
			}
		});
		window.dispatchEvent(new CustomEvent('closeMenu', {}));
	}, []);

	const isActiveSection = useCallback((elevatedSection) => {
		const pathname = window.location.pathname.substr(1);
		const firstSlashIndex = pathname.indexOf('/');
		const activeSection = firstSlashIndex > -1 ? pathname.substr(0, firstSlashIndex) : pathname;
		if (activeSection === elevatedSection) {
			return 'selected';
		}
	}, []);

	const itemTitle = useCallback((label) => {
		return label;
	}, []);

	const renderExternalLinkOption = useCallback((href, title, className, onClick, target) => {
		return (
			<MenuItem
				onClick={() => {
					onOptionClick();
					onClick && onClick();
				}}
			>
				<Link to={href} className={className} target={target ? target : '_blank'}>
					<EllipseControl content={title} tooltip={title} noOfLines={1} />
				</Link>
			</MenuItem>
		);
	}, []);

	const groupsConfig = useMemo(() => {
		const kronosBaseUrlRaw = env.getURL('kronos_web');
		const kronosBaseUrl = (kronosBaseUrlRaw || '').replace(/\/$/, '');

		const showALRADocumentLink = quickLinks.accountLevelRiskAssessmentDocumentId !== null,
			showUnderstandITProcessLink = quickLinks.itFlowISA315DocumentId !== null;

		const alraDocumentUrl = showALRADocumentLink && `/guidedworkflowv2/${quickLinks.accountLevelRiskAssessmentDocumentId}?engagementid=${engagementId}`,
			understandITProcessDocUrl = showUnderstandITProcessLink && `/guidedworkflowv2/${quickLinks.itFlowISA315DocumentId}?engagementid=${engagementId}`;

		const alraSummaryUrl = kronosBaseUrl + alraDocumentUrl,
			understandITProcessUrl = kronosBaseUrl + understandITProcessDocUrl;

		const showGroupStructureLink = quickLinks.hasGroupStructureSummary;
		const groupStructureUrl = quickLinks.groupStructureV2DocumentId
			? `${kronosBaseUrl}/guidedworkflowv2/${quickLinks.groupStructureV2DocumentId}?engagementid=${engagementId}`
			: baseCanvasUrl + '#groupauditstructuresummary';

		const showGroupInstructionLink = quickLinks.hasGroupInstructionSummary;
		const groupInstructionUrl = quickLinks.groupInstructionV2DocumentId
			? `${kronosBaseUrl}/guidedworkflowv2/${quickLinks.groupInstructionV2DocumentId}?engagementid=${engagementId}`
			: baseCanvasUrl + '#groupauditinstructionsummary';

		const showGroupInvolvementLink = quickLinks.groupInvolvementV2DocumentId !== null;
		const groupInvolvementUrl = quickLinks.groupInvolvementV2DocumentId
			? `${kronosBaseUrl}/guidedworkflowv2/${quickLinks.groupInvolvementV2DocumentId}?engagementid=${engagementId}`
			: baseCanvasUrl + '#groupauditinstructionsummary';

		const showScotSummaryLink = quickLinks.hasScotSummary;
		const scotSummaryUrl = quickLinks.scotSummaryV2DocumentId
			? `${kronosBaseUrl}/guidedworkflowv2/${quickLinks.scotSummaryV2DocumentId}?engagementid=${engagementId}`
			: baseCanvasUrl + '#scotsummary';

		const showCRASummaryLink = quickLinks.hasCRASummary;
		const cRASummaryUrl = quickLinks.craSummaryV2DocumentId
			? `${kronosBaseUrl}/guidedworkflowv2/${quickLinks.craSummaryV2DocumentId}?engagementid=${engagementId}`
			: baseCanvasUrl + '#crasummary';

		const showAuditPlanSummary = quickLinks.hasAuditPlanSummary;
		const auditPlanSummaryUrl = quickLinks.auditPlanV2DocumentId
			? `${kronosBaseUrl}/guidedworkflowv2/${quickLinks.auditPlanV2DocumentId}?engagementid=${engagementId}`
			: baseCanvasUrl + '#plan';

		return [
			{
				name: itemTitle(labels.scopeAndStrategy),
				openButtonProps: labels.scopeAndStrategy,
				disabled: isOfflineMode,
				options: [
					{
						show: quickLinks.hasAccountSummary && quickLinks.accountLevelRiskAssessmentDocumentId === null,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') +
									`${smartScreenRoutes.accountSummary}?engagementid=${engagementId}&documentid=${quickLinks && quickLinks.accountSummaryDocumentId}`,
								itemTitle(labels.accountSummary),
								isActiveSection(smartScreenRoutes.accountSummary),
								undefined,
								'_self'
							),
						title: itemTitle(labels.accountSummary)
					},
					{
						show: showALRADocumentLink,
						render: () => renderExternalLinkOption(alraSummaryUrl, itemTitle(labels.accountRiskAssessmentSummary), undefined, undefined, '_self')
					},
					{
						show: showScotSummaryLink,
						render: () => renderExternalLinkOption(scotSummaryUrl, itemTitle(labels.scotSummary), undefined, undefined, '_self')
					},
					{
						show: showCRASummaryLink,
						render: () => renderExternalLinkOption(cRASummaryUrl, itemTitle(labels.navCRASummary), undefined, undefined, '_self')
					},
					{
						show: quickLinks.hasITFlowISA315,
						render: () => renderExternalLinkOption(understandITProcessUrl, labels.understandITProcess)
					},
					{
						show: quickLinks.hasItSummary,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#itsummary', labels.navItSummary)
					},
					{
						show: quickLinks.hasSubstantiveEvaluationMatrix,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + `${smartScreenRoutes.sem}?engagementid=${engagementId}&documentid=${quickLinks && quickLinks.semDocumentId}`,
								itemTitle(labels.navSEM),
								isActiveSection(smartScreenRoutes.sem),
								undefined,
								'_self'
							),
						title: itemTitle(labels.navSEM)
					},
					{
						show: showAuditPlanSummary,
						render: () => renderExternalLinkOption(auditPlanSummaryUrl, labels.navAuditPlan, undefined, undefined, '_self')
					},
					{
						show: quickLinks.hasWorkPlanSummary,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#plan', labels.navWorkPlan, undefined, undefined, '_self')
					}
				]
			},
			{
				name: itemTitle(labels.navConclusion),
				openButtonProps: labels.navConclusion,
				disabled: isOfflineMode,
				options: [
					{
						show: quickLinks.hasClassicFindings,
						render: () => renderExternalLinkOption(baseCanvasUrl + '#findingsHub', labels.navFindings)
					},
					{
						show: true,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + `/reports/deficiencies?engagementid=${engagementId}`,
								itemTitle(labels.navDeficiencies),
								isActiveSection('reports'),
								undefined,
								'_self'
							)
					},
					{
						show: quickLinks.hasModificationsAfterRRD && quickLinks.modifiedAfterRRDDocumentId,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + `/smartscreens/440gl?engagementid=${engagementId}&documentid=${quickLinks.modifiedAfterRRDDocumentId}`,
								itemTitle(labels.nav440GL),
								isActiveSection(smartScreenRoutes.fourFortyGl),
								undefined,
								'_self'
							)
					},
					{
						show: quickLinks.hasIndependence,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/smartscreens/independence?engagementid=' + engagementId + '&documentid=' + quickLinks.independenceSummaryDocId,
								itemTitle(labels.navTeamMemberIndependence),
								isActiveSection(smartScreenRoutes.independence),
								undefined,
								'_self'
							)
					},
					{
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/archivetimeline?engagementid=' + engagementId,
								itemTitle(labels.navArchiveChecklist),
								undefined,
								undefined,
								'_self'
							)
					}
				]
			},
			{
				name: itemTitle(labels.navGroupAudit),
				openButtonProps: labels.navGroupAudit,
				disabled: isOfflineMode,
				show: quickLinks.hasGroupInstructionSummary || quickLinks.hasGroupStructureSummary || gaRoleTypeId,
				options: [
					{
						show: showGroupStructureLink,
						render: () => renderExternalLinkOption(groupStructureUrl, labels.navGroupStructureSummary, undefined, undefined, '_self')
					},
					{
						show: showGroupInstructionLink,
						render: () => renderExternalLinkOption(groupInstructionUrl, labels.navGroupInstructionSummary, undefined, undefined, '_self')
					},
					{
						show: showGroupInvolvementLink,
						render: () => renderExternalLinkOption(groupInvolvementUrl, labels.navGroupInvolvement, undefined, undefined, '_blank')
					},
					{
						show: (gaRoleTypeId === gaRoleTypes.primary || gaRoleTypeId === gaRoleTypes.component) && engagement.isGroupAuditEngagement,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/dashboard?engagementId=' + engagementId + '#groupStatus',
								labels.navGroupStatus,
								undefined
							)
					},
					{
						show: gaRoleTypeId === gaRoleTypes.regional,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/dashboard?engagementId=' + engagementId + '#primaryGroupStatus',
								labels.navPrimaryStatus,
								undefined
							)
					},
					{
						show: gaRoleTypeId === gaRoleTypes.regional,
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/dashboard?engagementId=' + engagementId + '#componentGroupStatus',
								labels.navComponentStatus,
								undefined
							)
					}
				]
			},
			{
				name: itemTitle(labels.navEngagementManagement),
				openButtonProps: labels.navEngagementManagement,
				disabled: isOfflineMode,
				options: [
					{
						show: quickLinks.hasV2Profile,
						render: () => renderExternalLinkOption(`guidedworkflowv2/${quickLinks.profileV2DocumentId}?engagementid=${engagementId}`, labels.navProfile)
					},
					{
						show: quickLinks.hasV2Profile === false && quickLinks.hasV1Profile,
						render: () =>
							renderExternalLinkOption(
								baseCanvasUrl +
									`&engagementdesc=${encodeURIComponent(engagementDescription)}#engagementprofile/${quickLinks.profileV1DocumentId}/${
										documentTypes.PROFILE
									}&referrer=${encodeURI(window.location.href)}`,
								labels.navProfile
							)
					},
					{
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/manageteam?engagementid=' + engagementId,
								itemTitle(labels.navHeaderManageTeam),
								isActiveSection(smartScreenRoutes.independence),
								undefined,
								'_self'
							)
					},
					{
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/contentupdates?engagementid=' + engagementId,
								itemTitle(labels.navContentUpdates),
								undefined,
								undefined,
								'_self'
							)
					},
					{
						render: () =>
							renderExternalLinkOption(
								env.getURL('hercules_web') + '/copyhub?engagementid=' + engagementId,
								itemTitle(labels.navCopyHub),
								undefined,
								undefined,
								'_self'
							)
					},
					{
						show: featureConfigurations?.features?.enableHerculesReports === false,
						render: () => renderExternalLinkOption(env.getURL('Zeus_web') + `/reportingHub?engagementid=${engagementId}`, itemTitle(labels.navReporting))
					},
					{
						show: featureConfigurations?.features?.enableHerculesReports === true,
						render: () => renderExternalLinkOption(env.getURL('hercules_web') + '/reports' + `?engagementid=${engagementId}`, itemTitle(labels.navReporting))
					}
				]
			}
		];
	}, [isOfflineMode, quickLinks, gaRoleTypeId, engagement, engagementId, engagementDescription, featureConfigurations]);

	const optionsConfig = useMemo(() => {
		return [
			{
				show: featureConfigurations?.features?.hasHelixProjects || featureConfigurations?.features?.isSmallDataImportEnabled,
				render: () =>
					renderExternalLinkOption(
						env.getURL('hercules_web') + `/connections?engagementId=${engagementId}`,
						labels.navHelixProjects,
						undefined,
						undefined,
						'_self'
					),
				disabled: isOfflineMode
			},
			{
				show: featureConfigurations?.features?.canvasEconomicsBase && configurations?.features?.enableEconomics === true,
				render: () => renderExternalLinkOption(featureConfigurations?.features?.canvasEconomicsBase, labels.navCanvasEconomics),
				disabled: isOfflineMode
			},
			{
				show: featureConfigurations?.features?.eyOversightBase && configurations?.features?.enableEYOversight === true,
				render: () => renderExternalLinkOption(featureConfigurations?.features?.eyOversightBase, labels.navOversight),
				disabled: isOfflineMode
			},
			{
				render: () => renderExternalLinkOption(env.getURL('help_web'), itemTitle(labels.navHelp)),
				title: itemTitle(labels.navHelp),
				disabled: isOfflineMode
			},
			{
				render: () => renderExternalLinkOption(env.getURL('validation_help_web'), itemTitle(labels.validationNavHelp)),
				disabled: isOfflineMode
			},
			{
				show: showQualtricsLink,
				render: () => renderQualtricsMotifLinkOption(itemTitle(labels.leaveUsFeedback))
			}
		];
	}, [quickLinks, featureConfigurations, engagementId, isOfflineMode]);

	const renderGroup = useCallback((group) => {
		const options = group.options.filter((option) => {
			return option.show === undefined || option.show;
		});
		if (group.disabled || options.length === 0) {
			return <StyledDisabledItem>{group.name}</StyledDisabledItem>;
		} else {
			return (
				<MenuItemGroup groupName={group.name} openButtonProps={group.openButtonProps}>
					{options?.length > 0 && (
						<React.Fragment>
							{options.map((option) => {
								return renderOption(option);
							})}
						</React.Fragment>
					)}
				</MenuItemGroup>
			);
		}
	}, []);

	const renderOption = useCallback((option) => {
		if (option.disabled) {
			return <StyledDisabledItem>{option.title}</StyledDisabledItem>;
		} else {
			if (option.href) {
				return (
					<MenuItem
						onClick={() => {
							onOptionClick();
							option.onClick && option.onClick();
						}}
					>
						<Link data-opennewtab to={option.href} className={option.className} target="_blank" rel="noopener noreferrer">
							<EllipseControl content={option.title} tooltip={option.title} noOfLines={1} />
						</Link>
					</MenuItem>
				);
			} else {
				return option.render && option.render();
			}
		}
	}, []);

	return (
		<React.Fragment>
			{groupsConfig.map((group) => {
				if (group.show === undefined || group.show) {
					return renderGroup(group);
				}
			})}
			{optionsConfig.map((option) => {
				if (option.show === undefined || option.show) {
					return renderOption(option);
				}
			})}
		</React.Fragment>
	);
}

const StyledDisabledItem = styled.section`
	position: relative;
	text-align: left;
	opacity: 0.5;
	pointer-events: none;
	height: var(--px-45);
	font-size: var(--px-16);
	padding: var(--px-15) 0 0 var(--px-19);
`;
